import { format } from "date-fns";
import { useMemo } from "react";
import { Button, Icon, Loading, Modal, Spinner, Tooltip, Typography } from "@/components/atoms";
import { getErrorData, getErrorMessages } from "@/helpers/reduxHelpers";
import { useProfile, useOperator } from "@/hooks";
import {
  useGetVehicleFormOptionsQuery,
  useUpdateRegistrationMutation,
  useVerifyVehicleMutation,
} from "@/redux/apis/vehicle/vehicleApi";
import { RawErrorResponse } from "@/redux/types";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, formatCurrency, getCountryFlagSrc } from "@/utils";
import { RegistrationRenewalNotice } from "../../common/RegistrationRenewalNotice";

interface ConfirmRegistrationChangeModalProps {
  vehicle: Vehicle;
  newPlate: string;
  newStateUuid: string;
  lookupVehicle?: Vehicle;
  open: boolean;
  onClose: () => void;
}

export const ConfirmRegistrationChangeModal = ({
  vehicle,
  newPlate,
  newStateUuid,
  lookupVehicle,
  open,
  onClose,
}: ConfirmRegistrationChangeModalProps) => {
  const { dateFormat } = useProfile();
  const [updateRegistration, { isLoading: isUpdating }] = useUpdateRegistrationMutation();
  const [verifyVehicle, { isLoading: isVerifying }] = useVerifyVehicleMutation();
  const { data: options } = useGetVehicleFormOptionsQuery();

  const fee = useMemo(() => options?.fees.verificationFee ?? 0, [options]);
  // The fee is billed in the provider's currency (PHP buildVehicleFeeCharge).
  const { currency: operatorCurrency } = useOperator();
  const isLoading = isUpdating || isVerifying;

  // Use lookup data if available, otherwise fall back to current vehicle
  const displayVehicle = lookupVehicle ?? vehicle;
  const flagSrc = getCountryFlagSrc(displayVehicle.country.countryCode);

  const plateChanged = newPlate !== vehicle.registrationNumber;
  const stateChanged = newStateUuid !== vehicle.state.uuid;

  const handleReconfirm = async () => {
    try {
      // 1. Actually update the registration
      await updateRegistration({
        vehicle_uuid: vehicle.uuid,
        registration_number: newPlate,
        state_uuid: newStateUuid,
      }).unwrap();

      // 2. Verify the vehicle
      await verifyVehicle(vehicle.uuid).unwrap();

      addToast("success", "Vehicle registration updated and reconfirmed successfully");
      onClose();
    } catch (e) {
      const errorData = getErrorData(e as RawErrorResponse);
      if (errorData?.error === "VIN_MISMATCH") {
        addToast("danger", getErrorMessages(e as RawErrorResponse)[0] || "VIN mismatch detected");
      } else {
        addToast("danger", getErrorMessages(e as RawErrorResponse)[0] || "Failed to update registration");
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] !p-6 md:w-[550px]">
      <div className="relative flex flex-col">
        {isLoading && <Loading />}

        <Typography variant="title">Confirm Updated Vehicle Details</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Review the details below to ensure everything matches your vehicle&apos;s latest registration and information before proceeding
        </Typography>
        <RegistrationRenewalNotice />

        <div className="mt-4 grid grid-cols-2 gap-3">
          <DetailRow label="Make" value={displayVehicle.model.make} />
          <DetailRow label="Model" value={displayVehicle.model.model} />
          <DetailRow
            label="Plate Number"
            value={
              plateChanged ? (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-danger" />
                  {newPlate}
                  <span className="text-info">(Updated)</span>
                </span>
              ) : (
                displayVehicle.registrationNumber
              )
            }
          />
          <DetailRow label="Year" value={displayVehicle.year?.toString() ?? "-"} />
          <DetailRow
            label="Color"
            value={
              displayVehicle.color ? (
                <span className="inline-flex items-center gap-2 capitalize">
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: displayVehicle.color.toLowerCase().replace("_", "") }}
                  />
                  {displayVehicle.color.toLowerCase().replace("_", " ")}
                </span>
              ) : (
                "-"
              )
            }
          />
          <DetailRow label="Engine Type" value={displayVehicle.model.engine ? displayVehicle.model.engine.toLowerCase() : "-"} />
          <DetailRow label="Passenger Capacity" value={`${displayVehicle.capacity.pax} pax`} />
          <DetailRow
            label="State Registered"
            value={
              stateChanged ? (
                <span className="inline-flex items-center gap-1">
                  {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
                  {displayVehicle.state.abbreviation}
                  <span className="text-info">(Updated)</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
                  {displayVehicle.state.abbreviation}
                </span>
              )
            }
          />
          <DetailRow
            label="Registration Expiry"
            value={displayVehicle.registration.expiryDate ? format(new Date(displayVehicle.registration.expiryDate), dateFormat) : "-"}
          />
          <DetailRow label="VIN" value={displayVehicle.vin ?? "-"} />
          <DetailRow
            label={
              <span className="inline-flex items-center gap-1">
                Vehicle Category
                <Tooltip content="System-defined category based on vehicle make/model" placement="top">
                  <Icon name="InfoCircle" variant="Bold" className="text-neutral-dark-gray" size="sm" />
                </Tooltip>
              </span>
            }
            value={displayVehicle.vehicleCategory?.name ?? "-"}
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleReconfirm} disabled={isLoading}>
            {isLoading ? <Spinner /> : `Reconfirm Registration for ${formatCurrency(fee, 2, operatorCurrency)}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
